import type { GroupedTask, MinifiedTask } from '@gcv/shared';
import { TaskInternalType } from '@gcv/shared';

import { BANK_CRB_TASKS_KEY, BANK_TASKS_KEY } from 'hooks/keys';
import { queryClient } from 'util/queryClient';
import { isGroupedTask } from '../../tasks/tasks.model';

/** The list caches a task can be resolved from: the tasks page and an account's tasks tab. */
export const TASK_LIST_KEYS = [BANK_TASKS_KEY, BANK_CRB_TASKS_KEY];

/**
 * Resolves a task from the loaded task lists, which carry the same task shape the detail endpoint
 * returns. Groups have to come from here — the detail endpoint is a lookup by task id and knows
 * nothing about group keys — and ungrouped tasks save the request.
 */
export const getCachedTask = (taskId: string): MinifiedTask | GroupedTask | undefined => {
  const matchesTask = (task: MinifiedTask | GroupedTask) => {
    if (!isGroupedTask(task)) return task.id === taskId;
    if (task.group_key === taskId) return true;

    // Monitoring reviews are opened by subtask id, since their group key is a synthesized string.
    return (
      task.tasks[0]?.internal_type === TaskInternalType.MONITORING_REVIEWS &&
      task.tasks.some((subtask) => subtask.id === taskId)
    );
  };

  const cachedTasks = TASK_LIST_KEYS.flatMap((taskListKey) =>
    queryClient
      .getQueriesData<(MinifiedTask | GroupedTask)[]>({ queryKey: [taskListKey] })
      .flatMap(([, tasks]) => tasks ?? [])
  );

  return cachedTasks.find(matchesTask);
};
